import React, { useCallback, useEffect, useState } from 'react';

import { FpsOpenChatInputBox } from '@atlassian/search-ai/fps-open-chat-input-box';

import { useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useSearchPageProactiveAiCohort } from '../../../controllers/proactive-ai-experiment';
import {
	useSharedIsSainAnswerExpanded,
	useSmartAnswersIntentStore,
} from '../../../controllers/smart-answers-intent-store';
import {
	useBootstrap,
	usePrimaryProduct,
	usePrimarySubProduct,
	useSearchSessionId,
} from '../../../controllers/store/bootstrap';
import { useSearchQuery } from '../../../controllers/store/search';

import { FpsFloatingContainer } from './fps-floating-container';

/** `data-testid` applied to the floating container wrapping the chat box. */
export const FLOATING_ROVO_CHAT_INPUT_TESTID = 'fps-floating-rovo-chat-input';

/**
 * `data-testid` of the FPS results column (`Body`'s main content). The floating
 * box matches this element's width so it lines up with the search results.
 */
const FPS_RESULTS_COLUMN_TESTID = 'search-page-body-main-content';

/**
 * Floating Rovo chat box for Full Page Search.
 *
 * Renders the shared {@link FpsOpenChatInputBox} (the "open chat" entry point)
 * inside an {@link FpsFloatingContainer}, which reveals it on scroll once the
 * user has moved past a fraction of the FPS results' scrollable distance.
 *
 * Gating: it is rendered only for users assigned to the `floating_chat_input`
 * cohort of the `search_page_proactive_ai_multi_variant` experiment. The cohort
 * is read (without firing an exposure — exposure is owned by
 * `SearchPageProactiveAiExperimentProvider`) via
 * {@link useSearchPageProactiveAiCohort}.
 *
 * Per the "FPS Rovo Chat Box Visibility Rules" doc, the floating box is hidden
 * while the SAIN answer is expanded
 */
export const FloatingRovoChatInput = (): React.JSX.Element | null => {
	const cohort = useSearchPageProactiveAiCohort();
	const [{ cloudId }] = useBootstrap();
	const [searchSessionId] = useSearchSessionId();
	const [query] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const [primarySubProduct] = usePrimarySubProduct();
	const {
		commonAttributes: { availableProducts },
	} = useCommonAndPrivateAttributes();
	const isSainExpanded = useSharedIsSainAnswerExpanded();
	const { setIsSainAnswerExpanded } = useSmartAnswersIntentStore();
	// Controls whether the whole floating container renders. The box manages its
	// own show/hide and fade-out internally
	const [isMounted, setIsMounted] = useState(true);

	// On every new search, re-show the box and re-arm the SAIN-expanded gate.
	// `isSainAnswerExpanded` latches `true` until a SAIN answer card remounts or
	// SmartAnswers unmounts — there is no user-facing collapse. So a subsequent
	// query that renders no SAIN answer would otherwise leave the flag stuck
	// `true` and keep this box hidden indefinitely; resetting it here guarantees
	// "new query ⇒ collapsed" regardless of whether SAIN renders. Submitting a
	// prompt separately dismisses the box for the *current* query.
	useEffect(() => {
		setIsMounted(true);
		setIsSainAnswerExpanded(false);
	}, [query, setIsSainAnswerExpanded]);

	const handleHideTransitionEnd = useCallback(() => setIsMounted(false), []);

	if (cohort !== 'floating_chat_input') {
		return null;
	}

	if (!isMounted) {
		return null;
	}

	// Hide the floating box while the SAIN answer is expanded
	if (isSainExpanded) {
		return null;
	}

	return (
		<FpsFloatingContainer
			testId={FLOATING_ROVO_CHAT_INPUT_TESTID}
			matchWidthTestId={FPS_RESULTS_COLUMN_TESTID}
		>
			<FpsOpenChatInputBox
				query={query}
				searchSessionId={searchSessionId}
				cloudId={cloudId}
				sourceProduct={primaryProduct}
				subproduct={primarySubProduct}
				availableProducts={availableProducts}
				isVisible
				onHideTransitionEnd={handleHideTransitionEnd}
			/>
		</FpsFloatingContainer>
	);
};
